import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    UserCheck,
    Check,
    X,
    Clock,
    Shield,
    AlertTriangle,
    Eye,
    Search,
    Filter,
    Download,
    RefreshCw,
    Wallet,
    FileText,
    Settings,
    Users
} from 'lucide-react';
import { promptToast } from '../../utils/sonnerDialogs';

const WhitelistManagement = () => {
    const navigate = useNavigate();
    const getDisplayName = () => 'Admin';
    const [whitelistRequests, setWhitelistRequests] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [loading, setLoading] = useState(true);

    // Whitelist request status constants
    const WHITELIST_STATUS = {
        PENDING: 'pending',
        APPROVED: 'approved',
        REJECTED: 'rejected',
        SUBMITTED: 'submitted'
    };

    // Load whitelist requests from localStorage
    useEffect(() => {
        const loadWhitelistRequests = () => {
            try {
                // Get whitelist requests created from allocation finalization
                const requests = JSON.parse(localStorage.getItem('whitelistRequests') || '[]');
                setWhitelistRequests(requests);
                setLoading(false);
            } catch (error) {
                console.error('Error loading whitelist requests:', error);
                setLoading(false);
            }
        };

        loadWhitelistRequests();

        // Set up interval to check for new requests every 5 seconds
        const interval = setInterval(loadWhitelistRequests, 5000);
        return () => clearInterval(interval);
    }, []);

    // Page access is controlled by route permissions - no action-level check needed
    if (false) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-center">
                    <h1 className="text-2xl font-bold text-gray-900 mb-4">Access Denied</h1>
                    <p className="text-gray-600">You don't have permission to access this page.</p>
                </div>
            </div>
        );
    }

    const handleApproveRequest = (requestId) => {
        const updatedRequests = whitelistRequests.map(request =>
            request.id === requestId
                ? {
                    ...request,
                    status: WHITELIST_STATUS.APPROVED,
                    approvedDate: new Date().toISOString(),
                    approvedBy: getDisplayName()
                }
                : request
        );

        setWhitelistRequests(updatedRequests);
        localStorage.setItem('whitelistRequests', JSON.stringify(updatedRequests));

        // Also update the related FOP transfer request status
        updateFOPTransferStatus(requestId, 'approved');
    };

    const handleRejectRequest = (requestId, reason) => {
        const updatedRequests = whitelistRequests.map(request =>
            request.id === requestId
                ? {
                    ...request,
                    status: WHITELIST_STATUS.REJECTED,
                    rejectedDate: new Date().toISOString(),
                    rejectedBy: getDisplayName(),
                    rejectionReason: reason || 'No reason provided'
                }
                : request
        );

        setWhitelistRequests(updatedRequests);
        localStorage.setItem('whitelistRequests', JSON.stringify(updatedRequests));

        // Also update the related FOP transfer request status
        updateFOPTransferStatus(requestId, 'rejected');
    };

    const updateFOPTransferStatus = (whitelistRequestId, action) => {
        try {
            const fopRequests = JSON.parse(localStorage.getItem('fopTransferRequests') || '[]');
            const updatedFopRequests = fopRequests.map(fopRequest => {
                if (fopRequest.whitelistRequestId === whitelistRequestId) {
                    return {
                        ...fopRequest,
                        status: action === 'approved' ? 'approved' : 'rejected',
                        updatedDate: new Date().toISOString()
                    };
                }
                return fopRequest;
            });
            localStorage.setItem('fopTransferRequests', JSON.stringify(updatedFopRequests));
        } catch (error) {
            console.error('Error updating FOP transfer status:', error);
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case WHITELIST_STATUS.PENDING:
                return <Clock className="h-4 w-4 text-yellow-500" />;
            case WHITELIST_STATUS.SUBMITTED:
                return <FileText className="h-4 w-4 text-blue-500" />;
            case WHITELIST_STATUS.APPROVED:
                return <Check className="h-4 w-4 text-green-500" />;
            case WHITELIST_STATUS.REJECTED:
                return <X className="h-4 w-4 text-red-500" />;
            default:
                return <Clock className="h-4 w-4 text-gray-500" />;
        }
    };

    const getStatusBadge = (status) => {
        const baseClasses = "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium";
        switch (status) {
            case WHITELIST_STATUS.PENDING:
                return (
                    <span className={`${baseClasses} bg-yellow-100 text-yellow-800`}>
                        <Clock className="w-3 h-3 mr-1" />
                        New Request
                    </span>
                );
            case WHITELIST_STATUS.SUBMITTED:
                return (
                    <span className={`${baseClasses} bg-blue-100 text-blue-800`}>
                        <FileText className="w-3 h-3 mr-1" />
                        Under Review
                    </span>
                );
            case WHITELIST_STATUS.APPROVED:
                return (
                    <span className={`${baseClasses} bg-green-100 text-green-800`}>
                        <Check className="w-3 h-3 mr-1" />
                        Approved
                    </span>
                );
            case WHITELIST_STATUS.REJECTED:
                return (
                    <span className={`${baseClasses} bg-red-100 text-red-800`}>
                        <X className="w-3 h-3 mr-1" />
                        Rejected
                    </span>
                );
            default:
                return (
                    <span className={`${baseClasses} bg-gray-100 text-gray-800`}>
                        <AlertTriangle className="w-3 h-3 mr-1" />
                        Unknown
                    </span>
                );
        }
    };

    // Filter requests based on search term and status
    const filteredRequests = whitelistRequests.filter(request => {
        const matchesSearch = !searchTerm ||
            request.investorName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            request.walletAddress?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            request.bondName?.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesStatus = statusFilter === 'all' || request.status === statusFilter;

        return matchesSearch && matchesStatus;
    });

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-MY', {
            style: 'currency',
            currency: 'MYR'
        }).format(amount);
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-MY');
    };

    const pendingCount = whitelistRequests.filter(r => r.status === WHITELIST_STATUS.PENDING).length;
    const submittedCount = whitelistRequests.filter(r => r.status === WHITELIST_STATUS.SUBMITTED).length;
    const approvedCount = whitelistRequests.filter(r => r.status === WHITELIST_STATUS.APPROVED).length;
    const rejectedCount = whitelistRequests.filter(r => r.status === WHITELIST_STATUS.REJECTED).length;

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <RefreshCw className="h-8 w-8 text-blue-600 animate-spin mx-auto mb-2" />
                    <p className="text-gray-600">Loading whitelist requests...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <div className="bg-white shadow">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center py-6">
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900">Whitelist Management</h1>
                            <p className="text-sm text-gray-600 mt-1">
                                System-wide whitelist administration and oversight
                            </p>
                        </div>
                        <div className="flex items-center space-x-4">
                            <button
                                onClick={() => navigate('/admin/security')}
                                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                            >
                                <Shield className="h-4 w-4 mr-2" />
                                Security Settings
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Stats Cards */}
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 mb-8">
                    <div className="bg-white overflow-hidden shadow rounded-lg">
                        <div className="p-5">
                            <div className="flex items-center">
                                <div className="flex-shrink-0">
                                    <Clock className="h-8 w-8 text-yellow-400" />
                                </div>
                                <div className="ml-5 w-0 flex-1">
                                    <dl>
                                        <dt className="text-sm font-medium text-gray-500 truncate">
                                            New Requests
                                        </dt>
                                        <dd className="text-lg font-medium text-gray-900">
                                            {pendingCount}
                                        </dd>
                                    </dl>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white overflow-hidden shadow rounded-lg">
                        <div className="p-5">
                            <div className="flex items-center">
                                <div className="flex-shrink-0">
                                    <FileText className="h-8 w-8 text-blue-400" />
                                </div>
                                <div className="ml-5 w-0 flex-1">
                                    <dl>
                                        <dt className="text-sm font-medium text-gray-500 truncate">
                                            Under Review
                                        </dt>
                                        <dd className="text-lg font-medium text-gray-900">
                                            {submittedCount}
                                        </dd>
                                    </dl>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white overflow-hidden shadow rounded-lg">
                        <div className="p-5">
                            <div className="flex items-center">
                                <div className="flex-shrink-0">
                                    <Check className="h-8 w-8 text-green-400" />
                                </div>
                                <div className="ml-5 w-0 flex-1">
                                    <dl>
                                        <dt className="text-sm font-medium text-gray-500 truncate">
                                            Approved
                                        </dt>
                                        <dd className="text-lg font-medium text-gray-900">
                                            {approvedCount}
                                        </dd>
                                    </dl>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white overflow-hidden shadow rounded-lg">
                        <div className="p-5">
                            <div className="flex items-center">
                                <div className="flex-shrink-0">
                                    <UserCheck className="h-8 w-8 text-blue-400" />
                                </div>
                                <div className="ml-5 w-0 flex-1">
                                    <dl>
                                        <dt className="text-sm font-medium text-gray-500 truncate">
                                            Total Requests
                                        </dt>
                                        <dd className="text-lg font-medium text-gray-900">
                                            {whitelistRequests.length}
                                        </dd>
                                    </dl>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Filters */}
                <div className="bg-white shadow rounded-lg mb-8">
                    <div className="px-6 py-4 border-b border-gray-200">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
                            <div className="relative max-w-xs">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <Search className="h-5 w-5 text-gray-400" />
                                </div>
                                <input
                                    type="text"
                                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                    placeholder="Search by investor, wallet, or bond..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>
                            <div className="flex items-center space-x-4">
                                <div className="flex items-center space-x-2">
                                    <Filter className="h-4 w-4 text-gray-400" />
                                    <select
                                        value={statusFilter}
                                        onChange={(e) => setStatusFilter(e.target.value)}
                                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                    >
                                        <option value="all">All Status</option>
                                        <option value={WHITELIST_STATUS.PENDING}>New Requests</option>
                                        <option value={WHITELIST_STATUS.SUBMITTED}>Under Review</option>
                                        <option value={WHITELIST_STATUS.APPROVED}>Approved</option>
                                        <option value={WHITELIST_STATUS.REJECTED}>Rejected</option>
                                    </select>
                                </div>
                                <button
                                    className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                                >
                                    <Download className="h-4 w-4 mr-2" />
                                    Export
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Requests Table */}
                <div className="bg-white shadow rounded-lg overflow-hidden">
                    <div className="px-6 py-4 border-b border-gray-200">
                        <h3 className="text-lg font-medium text-gray-900">Whitelist Requests</h3>
                        <p className="mt-1 text-sm text-gray-600">
                            {filteredRequests.length} of {whitelistRequests.length} requests
                        </p>
                    </div>

                    {filteredRequests.length === 0 ? (
                        <div className="text-center py-12">
                            <UserCheck className="mx-auto h-12 w-12 text-gray-400" />
                            <h3 className="mt-2 text-sm font-medium text-gray-900">No whitelist requests</h3>
                            <p className="mt-1 text-sm text-gray-500">
                                Whitelist requests will appear here when allocations are finalized.
                            </p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="table">
                                <thead className="table-header">
                                    <tr>
                                        <th className="table-header-cell">
                                            Investor Details
                                        </th>
                                        <th className="table-header-cell">
                                            Bond Information
                                        </th>
                                        <th className="table-header-cell">
                                            Wallet Address
                                        </th>
                                        <th className="table-header-cell">
                                            Allocation
                                        </th>
                                        <th className="table-header-cell">
                                            Status
                                        </th>
                                        <th className="table-header-cell">
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {filteredRequests.map((request) => (
                                        <tr key={request.id} className="hover:bg-gray-50">
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div>
                                                    <div className="text-sm font-medium text-gray-900">
                                                        {request.investorName}
                                                    </div>
                                                    <div className="text-sm text-gray-500">
                                                        ID: {request.investorId}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div>
                                                    <div className="text-sm font-medium text-gray-900">
                                                        {request.bondName}
                                                    </div>
                                                    <div className="text-sm text-gray-500">
                                                        Created: {formatDate(request.requestDate)}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex items-center">
                                                    <Wallet className="h-4 w-4 text-gray-400 mr-2" />
                                                    <div className="text-sm font-mono text-gray-900 break-all">
                                                        {request.walletAddress}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div>
                                                    <div className="text-sm font-medium text-gray-900">
                                                        {formatCurrency(request.tokenAmount)}
                                                    </div>
                                                    <div className="text-sm text-gray-500">
                                                        {request.allocationPercentage}%
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                {getStatusBadge(request.status)}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                                {(request.status === WHITELIST_STATUS.PENDING || request.status === WHITELIST_STATUS.SUBMITTED) ? (
                                                    <div className="flex space-x-2">
                                                        {true && (
                                                            <button
                                                                onClick={() => handleApproveRequest(request.id)}
                                                                className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-green-600 hover:bg-green-700"
                                                            >
                                                                <Check className="w-3 h-3 mr-1" />
                                                                Approve
                                                            </button>
                                                        )}
                                                        {true && (
                                                            <button
                                                                onClick={async () => {
                                                                    const reason = await promptToast('Reason for rejection (optional):', {
                                                                        placeholder: 'Enter optional reason',
                                                                        confirmLabel: 'Reject'
                                                                    });
                                                                    handleRejectRequest(request.id, reason);
                                                                }}
                                                                className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-red-600 hover:bg-red-700"
                                                            >
                                                                <X className="w-3 h-3 mr-1" />
                                                                Reject
                                                            </button>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <div className="text-gray-500 text-xs">
                                                        {request.status === WHITELIST_STATUS.APPROVED &&
                                                            `Approved by ${request.approvedBy} on ${formatDate(request.approvedDate)}`
                                                        }
                                                        {request.status === WHITELIST_STATUS.REJECTED &&
                                                            `Rejected by ${request.rejectedBy} on ${formatDate(request.rejectedDate)}`
                                                        }
                                                    </div>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {/* No page-level logout modal; logout is handled in the global navbar */}
        </div>
    );
};

export default WhitelistManagement;
